import { TOTAL_REPORT_CARD_AREAS } from './reportCard.config';

/** German school grade for a completed / incomplete area (1 = best, 6 = worst). */
export const COMPLETE_AREA_GRADE = 1;
const INCOMPLETE_AREA_GRADE = 6;

const gradeFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Formats a grade value as a 1-decimal German-comma string (e.g. `1` → `1,0`). */
export function formatGradeValue(value: number): string {
  return gradeFormatter.format(value);
}

export interface Grade {
  /** Rounded 1-decimal grade, or `null` when nothing has been completed yet. */
  value: number | null;
  /**
   * Locale-formatted numeric grade (e.g. `4,3`), or `null` when ungraded. The
   * ungraded copy ("Nicht bewertet") is user-facing text and is supplied by the
   * UI via i18n (Phase 2) — never hard-coded here.
   */
  label: string | null;
}

/**
 * Overall grade as the mean of the three areas, each scored 1 (complete) or 6
 * (incomplete) — the German school grading scale. Zero completions is reported
 * as ungraded (`value: null`) rather than the arithmetic 6,0.
 */
export function computeGrade(completedCount: number): Grade {
  if (completedCount <= 0) {
    return { value: null, label: null };
  }

  const completed = Math.min(completedCount, TOTAL_REPORT_CARD_AREAS);
  const incomplete = TOTAL_REPORT_CARD_AREAS - completed;
  const mean =
    (completed * COMPLETE_AREA_GRADE + incomplete * INCOMPLETE_AREA_GRADE) / TOTAL_REPORT_CARD_AREAS;
  const value = Math.round(mean * 10) / 10;

  return { value, label: formatGradeValue(value) };
}
